import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Loader2, Pencil, Trash2, Link2, Video, ExternalLink, Search } from "lucide-react";
import { toast } from "@/lib/toastBanner";
import usePersistentState from "@/hooks/usePersistentState";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResourceLinks() {
  const [resources, setResources] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = usePersistentState("resources:search", "");
  const [filterType, setFilterType] = usePersistentState("resources:filterType", "all");
  const [form, setForm] = useState({ title: "", url: "", link_type: "link", cohort_id: "", embed: false });

  const fetchResources = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/resources`, { withCredentials: true });
      setResources(data);
    } catch {
      toast.error("Failed to load resources");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCohorts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/cohorts`, { withCredentials: true });
      setCohorts(data.filter((c) => c.status === "active"));
    } catch (err) {
      console.error("Failed to load cohorts:", err);
    }
  }, []);

  useEffect(() => {
    fetchResources();
    fetchCohorts();
  }, [fetchResources, fetchCohorts]);

  const openAdd = () => {
    setEditTarget(null);
    setForm({ title: "", url: "", link_type: "link", cohort_id: "", embed: false });
    setShowModal(true);
  };

  const openEdit = (r) => {
    setEditTarget(r);
    setForm({
      title: r.title,
      url: r.url,
      link_type: r.link_type || "link",
      cohort_id: r.cohort_id || "",
      embed: r.embed || false,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.url.trim()) {
      toast.error("Title and URL are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        url: form.url.trim(),
        link_type: form.link_type,
        cohort_id: form.cohort_id || null,
        embed: form.link_type === "video" ? form.embed : false,
      };
      if (editTarget) {
        await axios.put(`${API}/resources/${editTarget.id}`, payload, { withCredentials: true });
        toast.success("Resource updated");
      } else {
        await axios.post(`${API}/resources`, payload, { withCredentials: true });
        toast.success("Resource created");
      }
      setShowModal(false);
      fetchResources();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const r = deleteTarget;
    if (!r) return;
    try {
      await axios.delete(`${API}/resources/${r.id}`, { withCredentials: true });
      toast.success("Resource deleted");
      fetchResources();
    } catch {
      toast.error("Delete failed");
      throw new Error("delete failed");
    }
  };

  const filtered = resources.filter((r) => {
    const matchSearch =
      !search ||
      r.title.toLowerCase().includes(search.toLowerCase()) ||
      r.url.toLowerCase().includes(search.toLowerCase());
    const matchType = filterType === "all" || r.link_type === filterType;
    return matchSearch && matchType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-[#6E7787]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="resource-links-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white" data-testid="resource-links-title">
            Resource Library
          </h1>
          <p className="text-sm text-[#6E7787] font-body mt-1">
            Manage links and videos shared with learners
          </p>
        </div>
        <Button
          onClick={openAdd}
          data-testid="add-resource-button"
          className="font-body text-white h-10 px-4 rounded-xl"
          style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Resource
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6E7787]" />
          <Input
            placeholder="Search resources..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="resource-search-input"
            className="pl-10 bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] h-10"
          />
        </div>
        <div className="flex gap-2">
          {["all", "link", "video"].map((t) => (
            <button
              key={t}
              onClick={() => setFilterType(t)}
              data-testid={`filter-${t}`}
              className={`px-3 py-2 text-sm font-body rounded-lg transition-colors ${
                filterType === t
                  ? "text-white bg-white/10"
                  : "text-[#6E7787] hover:text-white hover:bg-white/5"
              }`}
            >
              {t === "all" ? "All" : t === "link" ? "Links" : "Videos"}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-[#11141A] border border-white/10 rounded-xl">
          <Link2 className="h-10 w-10 text-[#6E7787] mx-auto mb-3" />
          <p className="text-[#6E7787] font-body">No resources found</p>
          <Button
            onClick={openAdd}
            variant="ghost"
            className="mt-3 text-sm font-body"
            style={{ color: "var(--primary-color, #4A90D9)" }}
            data-testid="empty-add-resource-button"
          >
            Add your first resource
          </Button>
        </div>
      ) : (
        <div className="bg-[#11141A] border border-white/10 rounded-xl overflow-hidden">
          <table className="w-full" data-testid="resources-table">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-5 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">
                  Title
                </th>
                <th className="text-left px-5 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider hidden sm:table-cell">
                  Type
                </th>
                <th className="text-left px-5 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider hidden md:table-cell">
                  Cohort
                </th>
                <th className="text-right px-5 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                  data-testid={`resource-row-${r.id}`}
                >
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      {r.link_type === "video" ? (
                        <Video className="h-4 w-4 text-red-400 shrink-0" />
                      ) : (
                        <Link2 className="h-4 w-4 shrink-0" style={{ color: "var(--primary-color)" }} />
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-white font-body font-medium truncate">{r.title}</p>
                          </div>
                        <a
                          href={r.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-[#6E7787] hover:text-white truncate block max-w-xs"
                        >
                          {r.url}
                        </a>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 hidden sm:table-cell">
                    <div className="flex items-center gap-1.5">
                      <Badge
                        variant="outline"
                        className={`text-xs font-body ${
                          r.link_type === "video"
                            ? "border-red-500/30 text-red-400"
                            : "border-white/20 text-[#A1A9B8]"
                        }`}
                      >
                        {r.link_type === "video" ? "Video" : "Link"}
                      </Badge>
                      {r.link_type === "video" && r.embed && (
                        <Badge className="text-xs font-body bg-purple-500/15 text-purple-400 border-purple-500/20">
                          Embed
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3.5 hidden md:table-cell">
                    <span className="text-sm text-[#A1A9B8] font-body">
                      {r.cohort_name || "All Cohorts"}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1">
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-[#6E7787] hover:text-white transition-colors rounded-lg hover:bg-white/5"
                        data-testid={`resource-open-${r.id}`}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                      <button
                        onClick={() => openEdit(r)}
                        className="p-2 text-[#6E7787] hover:text-white transition-colors rounded-lg hover:bg-white/5"
                        data-testid={`resource-edit-${r.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(r)}
                        className="p-2 text-[#6E7787] hover:text-red-400 transition-colors rounded-lg hover:bg-red-500/10"
                        data-testid={`resource-delete-${r.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#11141A] border-white/10 text-white max-w-md" data-testid="resource-modal">
          <DialogHeader>
            <DialogTitle className="font-body font-bold text-lg">
              {editTarget ? "Edit Resource" : "Add Resource"}
            </DialogTitle>
            <DialogDescription className="text-[#6E7787] font-body text-sm">
              {editTarget ? "Update the resource link details." : "Add a new link or video resource."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-[#A1A9B8] font-body text-sm">Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Clinical Guidelines PDF"
                data-testid="resource-title-input"
                className="bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] h-10"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#A1A9B8] font-body text-sm">URL</Label>
              <Input
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://..."
                data-testid="resource-url-input"
                className="bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] h-10"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#A1A9B8] font-body text-sm">Type</Label>
              <Select value={form.link_type} onValueChange={(v) => setForm({ ...form, link_type: v, embed: v === "video" ? form.embed : false })}>
                <SelectTrigger
                  className="bg-[#0A0C10] border-white/10 text-white h-10"
                  data-testid="resource-type-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#11141A] border-white/10">
                  <SelectItem value="link" className="text-white font-body">Link</SelectItem>
                  <SelectItem value="video" className="text-white font-body">Video</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.link_type === "video" && (
              <div className="space-y-2">
                <Label className="text-[#A1A9B8] font-body text-sm">Video Display</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, embed: false })}
                    data-testid="resource-display-external"
                    className={`flex-1 px-3 py-2.5 text-sm font-body rounded-lg border transition-colors ${
                      !form.embed
                        ? "border-white/20 bg-white/10 text-white"
                        : "border-white/10 bg-transparent text-[#6E7787] hover:text-white hover:border-white/15"
                    }`}
                  >
                    External Link
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, embed: true })}
                    data-testid="resource-display-embed"
                    className={`flex-1 px-3 py-2.5 text-sm font-body rounded-lg border transition-colors ${
                      form.embed
                        ? "border-purple-500/30 bg-purple-500/10 text-purple-400"
                        : "border-white/10 bg-transparent text-[#6E7787] hover:text-white hover:border-white/15"
                    }`}
                  >
                    Embed in Workspace
                  </button>
                </div>
                <p className="text-xs text-[#6E7787] font-body">
                  {form.embed
                    ? "Video will play inline within the learner workspace."
                    : "Video will open in a new browser tab."}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-[#A1A9B8] font-body text-sm">Cohort (optional)</Label>
              <Select
                value={form.cohort_id || "all"}
                onValueChange={(v) => setForm({ ...form, cohort_id: v === "all" ? "" : v })}
              >
                <SelectTrigger
                  className="bg-[#0A0C10] border-white/10 text-white h-10"
                  data-testid="resource-cohort-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#11141A] border-white/10">
                  <SelectItem value="all" className="text-white font-body">All Cohorts</SelectItem>
                  {cohorts.map((c) => (
                    <SelectItem key={c.id} value={c.id} className="text-white font-body">
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <Button
              variant="ghost"
              onClick={() => setShowModal(false)}
              className="font-body text-[#A1A9B8] hover:text-white"
              data-testid="resource-cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="resource-save-button"
              className="font-body text-white rounded-xl px-6"
              style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editTarget ? "Update" : "Create"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        tier={1}
        entityName={deleteTarget?.title}
        onConfirm={handleDelete}
      />
    </div>
  );
}
